import React, { useEffect, useState } from 'react';
import { IoMdClose } from 'react-icons/io';
import { FiCheck, FiCloud, FiEdit3, FiEye, FiGlobe } from 'react-icons/fi';
import {
  FaBook,
  FaRegFileExcel,
  FaRegFilePdf,
  FaRegFilePowerpoint,
  FaRegFileWord,
} from 'react-icons/fa6';

// Microsoft grants file access by place (sites, OneDrive), never by file
// type: the backend turns places and read/edit into scopes (scopes_for);
// file types are applied by Oqlous when files are listed.

const PLACES = [
  {
    key: 'sites',
    Icon: FiGlobe,
    label: 'SharePoint sites',
    hint: 'Sites you can open; you pick which ones',
  },
  {
    key: 'onedrive',
    Icon: FiCloud,
    label: 'My OneDrive',
    hint: 'Your own files',
  },
];

const MODES = [
  { write: false, Icon: FiEye, label: 'Read only' },
  { write: true, Icon: FiEdit3, label: 'Read and edit' },
];

const APPS = [
  { value: 'word', label: 'Word', Icon: FaRegFileWord, color: 'text-blue-400' },
  {
    value: 'excel',
    label: 'Excel',
    Icon: FaRegFileExcel,
    color: 'text-green-400',
  },
  {
    value: 'powerpoint',
    label: 'PowerPoint',
    Icon: FaRegFilePowerpoint,
    color: 'text-orange-400',
  },
  { value: 'pdf', label: 'PDF', Icon: FaRegFilePdf, color: 'text-red-400' },
];

export const DEFAULT_SHAREPOINT_ACCESS = {
  sites: true,
  onedrive: false,
  write: false,
  file_types: ['word', 'excel'],
  onenote: false,
};

const Section = ({ title, children }) => (
  <section className="mt-5">
    <h3 className="mb-2 text-xs font-semibold uppercase tracking-wide text-zinc-400">
      {title}
    </h3>
    {children}
  </section>
);

const selectedRing = (on) =>
  on
    ? 'border-[#5652CF] bg-[#5652CF]/10'
    : 'border-zinc-700 hover:border-zinc-500';

const SharePointAccessModal = ({
  initialAccess,
  onContinue,
  onClose,
  title = 'Connect SharePoint',
}) => {
  const [access, setAccess] = useState({
    ...DEFAULT_SHAREPOINT_ACCESS,
    ...(initialAccess || {}),
  });
  const [error, setError] = useState('');

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  const set = (patch) => {
    setError('');
    setAccess((prev) => ({ ...prev, ...patch }));
  };
  const toggleType = (value) =>
    set({
      file_types: access.file_types.includes(value)
        ? access.file_types.filter((t) => t !== value)
        : [...access.file_types, value],
    });

  const handleContinue = () => {
    if (!access.sites && !access.onedrive) {
      setError('Pick at least one place');
      return;
    }
    if (!access.file_types.length && !access.onenote) {
      setError('Pick at least one app');
      return;
    }
    onContinue(access);
  };

  const apps = [
    ...APPS.map((a) => ({
      ...a,
      on: access.file_types.includes(a.value),
      toggle: () => toggleType(a.value),
    })),
    {
      value: 'onenote',
      label: 'OneNote',
      Icon: FaBook,
      color: 'text-purple-400',
      on: access.onenote,
      toggle: () => set({ onenote: !access.onenote }),
    },
  ];

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-60 p-4">
      <div
        role="dialog"
        aria-labelledby="sharepoint-access-title"
        className="bg-1 flex max-h-[90vh] w-full max-w-lg flex-col overflow-y-auto rounded-xl border border-zinc-700 p-6"
      >
        <div className="flex items-start justify-between">
          <div className="flex items-center gap-3">
            <img
              src="/assets/icons/onedrive_icon.png"
              alt=""
              className="h-9 w-9 rounded-lg bg-zinc-200 p-1.5"
            />
            <div>
              <h2
                id="sharepoint-access-title"
                className="text-lg font-semibold text-zinc-100"
              >
                {title}
              </h2>
              <p className="text-xs text-zinc-400">
                Choose what Oqlous can reach. You can change this later.
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            aria-label="Close"
            className="rounded-md p-1 text-zinc-400 hover:bg-zinc-800 hover:text-zinc-200"
          >
            <IoMdClose size={20} />
          </button>
        </div>

        <Section title="Where">
          <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
            {PLACES.map(({ key, Icon, label, hint }) => {
              const on = access[key];
              return (
                <button
                  key={key}
                  type="button"
                  role="checkbox"
                  aria-checked={on}
                  onClick={() => set({ [key]: !on })}
                  className={`relative flex items-start gap-3 rounded-lg border p-3 text-left transition-colors ${selectedRing(on)}`}
                >
                  <Icon
                    size={18}
                    className={`mt-0.5 shrink-0 ${on ? 'text-[#9b98f5]' : 'text-zinc-400'}`}
                    aria-hidden="true"
                  />
                  <span>
                    <span className="block text-sm font-medium text-zinc-100">
                      {label}
                    </span>
                    <span className="block text-xs text-zinc-400">{hint}</span>
                  </span>
                  {on && (
                    <FiCheck
                      className="absolute right-2 top-2 text-[#9b98f5]"
                      size={14}
                      aria-hidden="true"
                    />
                  )}
                </button>
              );
            })}
          </div>
        </Section>

        <Section title="Access">
          <div
            role="radiogroup"
            className="grid grid-cols-2 gap-1 rounded-lg border border-zinc-700 p-1"
          >
            {MODES.map(({ write, Icon, label }) => {
              const on = access.write === write;
              return (
                <button
                  key={label}
                  type="button"
                  role="radio"
                  aria-checked={on}
                  onClick={() => set({ write })}
                  className={`flex items-center justify-center gap-2 rounded-md py-2 text-sm transition-colors ${
                    on
                      ? 'bg-zinc-700 font-medium text-zinc-100'
                      : 'text-zinc-400 hover:text-zinc-200'
                  }`}
                >
                  <Icon size={14} aria-hidden="true" /> {label}
                </button>
              );
            })}
          </div>
          {access.write && (
            <p className="mt-2 text-xs text-zinc-400">
              Every change waits for your approval before it's saved.
            </p>
          )}
        </Section>

        <Section title="Apps">
          <div className="flex flex-wrap gap-2">
            {apps.map(({ value, label, Icon, color, on, toggle }) => (
              <button
                key={value}
                type="button"
                role="checkbox"
                aria-checked={on}
                onClick={toggle}
                className={`flex items-center gap-2 rounded-full border px-3 py-1.5 text-sm transition-colors ${selectedRing(on)} ${
                  on ? 'text-zinc-100' : 'text-zinc-400'
                }`}
              >
                <Icon
                  size={14}
                  className={on ? color : ''}
                  aria-hidden="true"
                />
                {label}
                {on && (
                  <FiCheck
                    size={12}
                    className="text-[#9b98f5]"
                    aria-hidden="true"
                  />
                )}
              </button>
            ))}
          </div>
        </Section>

        {error && (
          <p role="alert" className="mt-4 text-sm text-red-400">
            {error}
          </p>
        )}

        <div className="mt-6 flex justify-end gap-2 border-t border-zinc-800 pt-4">
          <button
            onClick={onClose}
            className="rounded-md border border-zinc-600 px-4 py-2 text-sm text-zinc-300 hover:bg-zinc-800"
          >
            Cancel
          </button>
          <button
            onClick={handleContinue}
            className="rounded-md bg-[#5652CF] px-4 py-2 text-sm font-medium text-white hover:bg-[#4642bf]"
          >
            Continue to Microsoft
          </button>
        </div>
      </div>
    </div>
  );
};

export default SharePointAccessModal;
